import type { UserRole } from '@/types';

// Temporary launch focus: Built to Close is the canonical public auth brand.
// Generic sign-ins still enter through the role router so staff retain their
// normal homes while course learners continue onward to the learner app.
// Keep these together so the month-long focus can be reversed without hunting
// through every auth method independently.
export const BUILT_TO_CLOSE_SLUG = 'built-to-close';
export const BUILT_TO_CLOSE_LOGIN_PATH = `/t/${BUILT_TO_CLOSE_SLUG}/login`;
export const BUILT_TO_CLOSE_REDEEM_PATH = `/t/${BUILT_TO_CLOSE_SLUG}/course/redeem`;
export const ROLE_ROUTER_HOME_PATH = '/dashboard';
export const COURSE_LEARNER_HOME_PATH = '/lms';
export const ADMIN_PLATFORM_HOME_PATH = '/admin/platform/overview';
export const EMPLOYER_HOME_PATH = '/employer';

const ROLE_HOME_PATHS = {
  rep: ROLE_ROUTER_HOME_PATH,
  sales_manager: ROLE_ROUTER_HOME_PATH,
  client_admin: ROLE_ROUTER_HOME_PATH,
  super_admin: ADMIN_PLATFORM_HOME_PATH,
  editor: ROLE_ROUTER_HOME_PATH,
  course_learner: COURSE_LEARNER_HOME_PATH,
  employer: EMPLOYER_HOME_PATH,
  support_operator: ROLE_ROUTER_HOME_PATH,
} satisfies Record<UserRole, string>;

/**
 * Canonical landing page for each supported primary/effective application role.
 *
 * Accepts a string so runtime callers can fail closed if persisted auth data
 * somehow contains a role newer than this deployment. `ROLE_HOME_PATHS` still
 * uses `satisfies Record<UserRole, string>`, so adding a declared role without
 * choosing its home is a compile-time failure.
 */
export function homePathForRole(role: string): string | null {
  return Object.prototype.hasOwnProperty.call(ROLE_HOME_PATHS, role)
    ? ROLE_HOME_PATHS[role as UserRole]
    : null;
}

export const COURSE_LEARNER_RECOVERY_PATH =
  `/auth/set-password?type=recovery&next=${COURSE_LEARNER_HOME_PATH}`;

export const COURSE_LEARNER_INVITE_PATH =
  `/auth/set-password?next=${COURSE_LEARNER_HOME_PATH}`;

// Tenant-team invitations are not learner invitations. Carry the role router
// explicitly so the learner-focused set-password fallback cannot send reps,
// managers, or client admins into `/lms`.
export const TEAM_MEMBER_INVITE_PATH = '/auth/set-password?next=/dashboard';

export function inviteSetPasswordPath(nextPath: string): string {
  const params = new URLSearchParams({ next: nextPath });
  return `/auth/set-password?${params.toString()}`;
}

export function recoverySetPasswordPath(nextPath: string): string {
  const params = new URLSearchParams({
    type: 'recovery',
    next: nextPath,
  });
  return `/auth/set-password?${params.toString()}`;
}
